<template>
  <the-dialog-box
    ref="dialogBox"
    :submit="handleSubmit"
    :is-submit-btn="!isView"
    :is-secondary-btn="!!(item && item.id && item.inventory_class_id == inventoryClassIdInst && (item.id > 0))"
    :secondary-submit="viewProofOfWork"
    :secondary-btn-text="'View Proof of Work'"
    :is-cancel-btn="!isProofOfWorkNeeded"
    :full-screen="true"
    persistent
    :submit-btn-text="pickState ? 'Confirm' : 'Submit'"
  >
    <template slot="header">
      <span>{{ pickListFormHeaderText }}</span>
    </template>
    <template slot="body">
      <v-row>
        <v-col v-if="!isCreatingFromTruckScheduleRequest" cols="2" class="pb-0">
          <inventory-class-select-element
            v-model="item.inventory_class_id"
            :disabled="!roles.includes(userRoleIdAdmin) && !roles.includes(userRoleIdAssociate) && !roles.includes(userRoleIdRequester) || pickState || isSupplyingForBillOfLading || isView || isBackorderPickList || (item && item.items ? item.items.length > 0 : false)"
            :is-required="true"
            :is-add-all="false"
            :is-railings="false"
            :is-glass="false"
            :is-vwd-glass="false"
            :is-panels="false"
            :is-sid="false"
            :is-rail-part="false"
            :is-paint="false"
            :is-equipment="false"
            :is-patio-door="false"
            :is-euro-door="false"
            :is-medical-aid="false"
          />
        </v-col>
        <v-col
          v-if="!isCreatingFromTruckScheduleRequest"
          class="pb-0"
          cols="2"
        >
          <v-autocomplete
            v-model="item.project_id"
            :items="projects"
            item-value="id"
            item-text="name"
            label="Project"
            :rules="[(v) => !!v || 'Project is required']"
            :disabled="!roles.includes(userRoleIdAdmin) && !roles.includes(userRoleIdAssociate) && !roles.includes(userRoleIdRequester) || pickState || isSupplyingForBillOfLading || isView || isBackorderPickList"
            :loading="loadingProjects"
            @change="onProjectChange"
          >
            <template #label>
              Project<RedAsterisk />
            </template>
          </v-autocomplete>
        </v-col>
        <v-col
          v-if="!isCreatingFromTruckScheduleRequest"
          class="pb-0"
          cols="3"
        >
          <v-autocomplete
            v-model="item.job_id"
            :label="jobLabel"
            :items="jobs"
            :item-text="item => `${item.code} - ${item.name}; ${item.reference}`"
            item-value="id"
            :loading="loadingJobs"
            :disabled="!roles.includes(userRoleIdAdmin) && !roles.includes(userRoleIdAssociate) && !roles.includes(userRoleIdRequester) || pickState || isSupplyingForBillOfLading || isView || isBackorderPickList"
            clearable
            :rules="item.inventory_class_id === inventoryClassIdInst ? [(v) => !!v || jobLabel + ' is required'] : []"
            @change="onJobChange"
          >
            <template #label>
              {{ jobLabel }}<RedAsterisk v-if="item.inventory_class_id === inventoryClassIdInst"/>
            </template>
            <template #selection="{ item }">
              <v-chip
                v-if="item.sub_class_color || item.sub_class_name"
                class="mt-1 mb-1 mr-3"
                small
                text-color="white"
                style="cursor: pointer;"
                :color="item && item.sub_class_color ? item.sub_class_color : 'white'"
              >
                {{ item && item.sub_class_name ? item.sub_class_name : '' }}
              </v-chip>
              <span>
                <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }} <span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
              </span>
            </template>
            <template #item="{ item }">
              <v-chip
                v-if="item.sub_class_color || item.sub_class_name"
                class="mt-1 mb-1 mr-3"
                small
                text-color="white"
                style="cursor: pointer;"
                :color="item && item.sub_class_color ? item.sub_class_color : 'white'"
              >
                {{ item && item.sub_class_name ? item.sub_class_name : '' }}
              </v-chip>
              <span>
                <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }} <span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
              </span>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col
          v-if="!isCreatingFromTruckScheduleRequest"
          class="pb-0"
          cols="2"
        >
          <v-autocomplete
            v-model="item.department_id"
            :items="departments"
            item-value="id"
            label="Department"
            item-text="name"
            :rules="item.truck_schedule_request_id || isBackorderPickList || isSupplyingForBillOfLading ? [] : [(v) => !!v || 'Department is required']"
            :disabled="(!roles.includes(userRoleIdAdmin) && !roles.includes(userRoleIdAssociate) && !roles.includes(userRoleIdRequester)) || !!item.id || isSupplyingForBillOfLading || isView || isBackorderPickList"
            :loading="loadingDepartments"
            @change="(department) => getDefaultQrCodes(department)"
          >
            <template #label>
              Department<RedAsterisk v-if="!item.truck_schedule_request_id && !isBackorderPickList && !isSupplyingForBillOfLading" />
            </template>
          </v-autocomplete>
        </v-col>
        <v-spacer />
        <v-col
          v-if="(roles.includes(userRoleIdAdmin) || roles.includes(userRoleIdAssociate) || roles.includes(userRoleIdRequester)) && !pickState && !isSupplyingForBillOfLading && item.inventory_class_id !== inventoryClassIdInst"
          class="text-end mt-4 pb-0"
        >
          <v-btn
            color="primary"
            outlined
            @click="add"
            :disabled="(!isCreatingFromTruckScheduleRequest && !item.inventory_class_id) || isView || loadingNewItemBtn || loadingQrCodes"
            :loading="loadingNewItemBtn || loadingQrCodes"
          >
            <v-icon
              small
              class="mr-1"
            >
              fas fa-plus
            </v-icon>
            New Item
            <template #loader>
              <span>Loading...</span>
            </template>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card
            elevation="0"
            outlined
          >
            <v-data-table
              v-model="selectedPickListItems"
              :headers="headers"
              :items="item.items"
              :show-select="isSupplyingForBillOfLading"
              hide-default-footer
              disable-pagination
              dense
              item-key="inventory_tag"
              :loading="loading || loadingDefaultQrCodes"
            >
              <template #[`item.inventory_tag`]="{ item }">
                <span><span style="font-weight: bold;">{{ !isCreatingFromTruckScheduleRequest && item.inventory_tag ? `${item.inventory_tag} -` : '' }}</span> {{ item.inventory_tag_item ? getItemName(item.inventory_tag_item) : '' }}</span>
              </template>
              <template #[`item.quantity_required`]="{ item }">
                <v-text-field
                  v-model="item.quantity_required"
                  :disabled="pickState || isSupplyingForBillOfLading || isView"
                  outlined
                  :hide-details="true"
                  dense
                  single-line
                  type="number"
                  min="0"
                  :max="item.quantity_on_hand"
                  label="Enter Quantity"
                  class="mt-1 mb-1"
                  clearable
                />
              </template>
              <template #[`item.photo`]="{ item }">
                <a v-if="item.inventory_tag_item && item.inventory_tag_item.item && item.inventory_tag_item.item.photo"
                  @click="openItemShowImageForm(item)"
                >
                  <!-- <v-img
                    :src="convertedPhoto(item.inventory_tag)"
                    class="profile-photo"
                  /> -->
                  <v-icon
                    small
                    color="primary"
                  >
                    fa-image
                  </v-icon>
                </a>
              </template>
              <template #[`item.status`]="{ item }">
                <v-chip
                  v-if="item.status"
                  class="mt-1 mb-1 mr-3"
                  small
                  text-color="white"
                  style="cursor: pointer;"
                  :color="item.status ? item.status.color : ''"
                >
                  {{ item.status ? item.status.name : '' }}
                </v-chip>
              </template>
              <template v-if="isSupplyingForBillOfLading" #[`item.bill_of_lading_id`]="{ item }">
                <span v-if="item.bill_of_lading_id" style="font-weight: bold;">{{`B${item.bill_of_lading_id}`}}</span>
                <span v-else>-</span>
              </template>
              <template #[`item.actions`]="{ item }" v-if="!isSupplyingForBillOfLading">
                <v-btn
                  v-if="(roles.includes(userRoleIdAdmin) || roles.includes(userRoleIdAssociate) || roles.includes(userRoleIdPicker)) && item.id && item.status && item.status.id == itemStatusIdNotSupplied && pickState"
                  class="ml-1 mt-1 mb-1"
                  color="primary"
                  outlined
                  small
                  :disabled="isView"
                  @click="pick(item)"
                >
                  PICK
                </v-btn>
                <v-btn
                  v-if="(roles.includes(userRoleIdAdmin) || roles.includes(userRoleIdAssociate) || roles.includes(userRoleIdPicker)) && item.id && item.status && item.status.id != itemStatusIdNotSupplied && pickState"
                  class="ml-1 mt-1 mb-1"
                  color="primary"
                  outlined
                  small
                  :disabled="isView"
                  @click="pick(item, true)"
                >
                  EDIT PICK
                </v-btn>
                <v-btn
                  v-if="(roles.includes(userRoleIdAdmin) || roles.includes(userRoleIdAssociate) || roles.includes(userRoleIdRequester)) && (item.status ? item.status.id == itemStatusIdNotSupplied : true) && !item.is_default && !pickState"
                  class="ml-1 mt-1 mb-1"
                  color="primary"
                  outlined
                  small
                  :disabled="isView"
                  @click="openEditItem(item.inventory_tag)"
                >
                  EDIT
                </v-btn>
                <v-btn
                  v-if="(roles.includes(userRoleIdAdmin) || roles.includes(userRoleIdAssociate) || roles.includes(userRoleIdRequester)) && (item.status ? item.status.id == itemStatusIdNotSupplied : true)  && !item.is_default && !pickState"
                  class="ml-1 mt-1 mb-1"
                  color="error"
                  outlined
                  small
                  :disabled="isView"
                  @click="deleteItem(item)"
                >
                  DELETE
                </v-btn>
              </template>
              <template #[`item.data-table-select`]="{ item }">
                <v-checkbox
                  v-if="!item.bill_of_lading_id && item.is_supplied === 1"
                  v-model="selectedPickListItems"
                  :value="item"
                />
              </template>
              <template slot="body.append">
                <tr v-if="loadingDefaultQrCodes">
                  <td colspan="10" class="text-center">
                    <span style="font-weight: bold; font-size: 14px;">
                      Loading Default QR Codes...
                    </span>
                  </td>
                </tr>
                <tr>
                  <th v-if="isSupplyingForBillOfLading" />
                  <th />
                  <th />
                  <th />
                  <th />
                  <th>
                    <span :style="{ color: isDarkMode ? '' : '#000000', fontWeight: 'bold', fontSize: '14px', display: 'flex', justifyContent: 'flex-end' }">Totals:</span>
                  </th>
                  <th>
                    <span
                      class="pl-3"
                      :style="{ color: isDarkMode ? '' : '#000000', fontWeight: 'bold', fontSize: '14px' }"
                      style="color: #000000; font-weight: bold; font-size: 14px;"
                    >{{ getTotalRequired() }}</span>
                  </th>
                  <th>
                    <span
                      :style="{ color: isDarkMode ? '' : '#000000', fontWeight: 'bold', fontSize: '14px' }"
                      style="color: #000000; font-weight: bold; font-size: 14px;"
                    >{{ getTotalSupplied() }}</span>
                  </th>
                  <th />
                  <th />
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <bill-of-lading-extra-item-form
            ref="billOfLadingExtraItemForm"
            :is-pick-list-form="true"
            :is-supplying-for-bill-of-lading="isSupplyingForBillOfLading"
            :is-view="isView"
            :is-pick-state="pickState"
            @custom-item-supplied="customItemSupplySuccess"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-form ref="form">
            <v-card>
              <v-card-title>
                <v-row>
                  <v-col>
                    <span style="font-size: 20px; font-weight: 500;">Attachments</span>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col class="pb-0">
                    <v-file-input v-model="attachmentInput" outlined small-chips>
                      <template slot="label">
                        Attachment
                      </template>
                    </v-file-input>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="text-end pt-0">
                    <v-btn color="primary" @click="addAttachment()" :disabled="this.attachmentInput === null">
                      ADD ATTACHMENT
                    </v-btn>
                  </v-col>
                </v-row>
                <div v-if="attachments.length > 0">
                  <v-row>
                    <v-col class="pb-0 pt-0 ">
                      <zig-zag />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-row>
                        <v-col>
                          <v-card-text style="max-height: 500px; overflow-y: auto; border: 1px solid #d3d3d3; border-radius: 3px;">
                            <v-list two-line class="pt-0 pb-0">
                              <div v-for="(file, index) in attachments" :key="'attachment_' + index">
                                <v-list-item>
                                  <v-list-item-content>
                                    <v-img v-if="file.photo" :src="convertedPhoto(file.photo, file.mime_type)" contain max-width="500"/>
                                    <span v-else>
                                      {{ file.original_file_name || file.file_name || file.name }}
                                    </span>
                                    <v-list-item-subtitle style="padding-top: 10px;">
                                      {{ file.created_at || "Not Saved" }}
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle v-if="file.user">
                                      {{ `${file.user.first_name ? file.user.first_name : ''} ${file.user.last_name ? file.user.last_name : ''}` }}
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle v-else>
                                      {{ user.first_name }} {{ user.last_name }}
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle class="text-end">
                                      <v-btn v-if="file.id" color="purple" class="mr-2" outlined small @click="downloadAttachment(file)">
                                        <v-icon small class="mr-1">fas fa-download</v-icon>
                                      </v-btn>
                                      <v-btn color="error" outlined small @click="deleteAttachment(index)">
                                        <v-icon small class="mr-1">fas fa-trash</v-icon>
                                      </v-btn>
                                    </v-list-item-subtitle>
                                  </v-list-item-content>
                                </v-list-item>
                                <v-divider v-if="index < attachments.length - 1"/>
                              </div>
                            </v-list>
                          </v-card-text>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
            </v-card>
          </v-form>
        </v-col>
      </v-row>
      <v-spacer />
      <v-row>
        <v-col>
          <v-row align="center">
            <v-col class="pb-0">
              <span :style="{ color: '#000000', fontSize: '18px' }"
                >Notes</span
              >
            </v-col>
            <v-col v-if="item.id" class="text-end">
              <v-btn color="orange" outlined @click="openNoteForm">
                SEE ALL NOTES
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pt-0">
              <v-textarea
                v-model="item.note"
                :disabled="item.notes && item.notes.length > 0"
                outlined
                rows="3"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="isProofOfWorkNeeded">
        <v-spacer />
        <v-col cols="4">
          <v-file-input
            v-model="attachment"
            show-size
          >
            <template slot="label">
              Proof Of Work
            </template>
          </v-file-input>
        </v-col>
      </v-row>
      <part-pick-list-item-form
        ref="itemForm"
        :is-creating-from-truck-schedule-request="isCreatingFromTruckScheduleRequest"
        @success="save"
      />
      <part-pick-list-item-view-form
        ref="itemViewForm"
        :is-backorder-pick-list="isBackorderPickList"
        @success="supplySuccess"
      />
      <inventory-item-show-image-form
        ref="itemShowImageForm"
      />
      <attachment-form
        v-show="false"
        ref="attachmentForm"
        :title="'Pick List Proof Of Work'"
        :add-btn-text="'Proof Of Work'"
        :attachment-input-label="'Proof Of Work'"
        :show-images="true"
        :is-delete="false"
        :is-add="false"
      />
      <note-form v-show="false" ref="noteForm" @success="handleNoteSuccess" />
      <confirm-form ref="confirmForm" />
    </template>
  </the-dialog-box>
</template>

<script>
import { saveAs } from 'file-saver';
import { mapState, mapGetters } from 'vuex';
import PartPickListItemForm from '@/components/forms/partPickLists/PartPickListItemForm';
import PartPickListItemViewForm from '@/components/forms/partPickLists/PartPickListItemViewForm';
import BillOfLadingExtraItemForm from '@/components/forms/billOfLadings/BillOfLadingExtraItemForm';
import InventoryItemShowImageForm from '@/components/forms/InventoryItemShowImageForm';
import NoteForm from '@/components/forms/notes/NoteForm';
import AttachmentForm from '@/components/elements/AttachmentForm';
import ZigZag from '@/components/elements/ZigZag';

export default {
  components: {
    PartPickListItemForm,
    PartPickListItemViewForm,
    BillOfLadingExtraItemForm,
    InventoryItemShowImageForm,
    NoteForm,
    AttachmentForm,
    ZigZag,
  },
  props: {
    isSupplyingForBillOfLading: {
      type: Boolean,
      default: false,
    },
    isCreatingFromTruckScheduleRequest: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    item: null,
    itemDto: {
      id: 0,
      inventory_class_id: null,
      project_id: null,
      job_id: null,
      department_id: null,
      items: [],
      bill_of_lading_extra_items: [],
      note: null,
    },

    attachment: null,
    pickedItems: [],
    selectedPickListItems: [],

    attachmentInput: null,
    attachments: [],

    departments: [],
    filteredDepartments: [],
    projects: [],
    jobs: [],
    qrCodes: [],
    roles: [],

    inventoryClassId4500: 1,
    inventoryClassId4800: 2,
    inventoryClassIdInst: 4,
    inventoryClassIdCommercial: 6,
    inventoryClassIdParts: 11,
    userRoleIdAdmin: 3,
    userRoleIdAssociate: 6,
    userRoleIdRequester: 15,
    userRoleIdPicker: 23,
    statusIdNotSupplied: 1,
    statusIdPartiallySupplied: 2,
    statusIdSupplied: 3,
    itemStatusIdSupplied: 1,
    itemStatusIdNotSupplied: 2,
    itemStatusIdPartiallySupplied: 3,
    noteTypeIdPickList: 6,
    departmentId4800Installation: 49,
    departmentId6000Installation: 51,
    attachmentTypeIdPickList: 4,

    loading: false,
    loadingQrCodes: false,
    loadingDepartments: false,
    loadingProjects: false,
    loadingJobs: false,
    loadingNewItemBtn: false,
    loadingDefaultQrCodes: false,
    pickState: false,
    isView: false,
  }),
  computed: {
    jobLabel() {
      return this.$store.getters['businessRulesGetBoolean']('Jobs Rename to Release')
        ? 'Release'
        : 'Job'; 
    },
    ...mapState({
      user: (state) => state.user,
      isDarkMode: 'isDarkMode',
    }),
    ...mapGetters([

    ]),
    headers() {
      const items = [];

      items.push({ text: 'Location', value: 'inventory_tag_item.location.name', width: '10%' });
      items.push({ text: 'Shelf', value: 'inventory_tag_item.inventory_location.name', width: '10%' });
      items.push({ text: 'QR Code - Item', value: 'inventory_tag', width: '30%' });
      items.push({ text: 'UOM', value: 'inventory_tag_item.uom.name', width: '5%' });
      if (this.item.inventory_class_id === this.inventoryClassIdInst) {
        items.push({ text: 'Photo', value: 'photo', width: '5%' });
      }
      items.push({ text: 'Qty on Hand', value: 'quantity_on_hand', width: '5%' });
      items.push({ text: 'Qty Required', value: 'quantity_required', width: '10%' });
      items.push({ text: 'Qty Supplied', value: 'quantity_supplied', width: '10%' });
      items.push({ text: 'Status', value: 'status', width: '10%', align: 'center' });
      if (this.isSupplyingForBillOfLading) {
        items.push({ text: 'BOL', value: 'bill_of_lading_id', width: '5%', align: 'center' }); 
      }
      if (!this.isSupplyingForBillOfLading) {
        items.push({
          text: 'Actions', value: 'actions', align: 'end', width: '20%',
        });
      }

      return items;
    },

    pickListFormHeaderText() {
      if (this.item && this.item.id) {
        if (this.pickState || this.isView || this.isSupplyingForBillOfLading) return `Pick List #${this.item.id}`;
        return `Edit Pick List #${this.item.id}`;
      }
      if (this.isCreatingFromTruckScheduleRequest) {
        if (this.item && this.item.items && this.item.items.length > 0 && this.item.items[0].id) {
          return 'Pick List';
        }
      }
      return 'Create Pick List';
    },

    isBackorderPickList() {
      return this.item && this.item.backorder_id && this.item.backorder_id > 0;
    },

    isProofOfWorkNeeded() {
      // Proof of work is currently optional for web but required for mobile
      return this.item && this.item.inventory_class_id === this.inventoryClassIdInst && this.pickedItems.length > 0 && this.pickedItems.some((item) => item.quantity_supplied > 0);
    },
  },
  watch: {
    'item.inventory_class_id': {
      async handler(newVal, oldVal) {
        if (this.isInit) return;
        if (newVal !== oldVal) {
          this.item.items = [];
          this.getDepartments();
        }
        if (newVal !== null && newVal !== this.inventoryClassIdInst) {
          await this.getQrCodes();
        }
        if (oldVal === this.inventoryClassIdInst && newVal === this.inventoryClassIdParts && this.item.department_id) {
          this.getDefaultQrCodes(this.item.department_id);
        }
        // if (newVal === this.inventoryClassIdInst) {
        //   this.item.department_id = this.departmentIdInstallation;
        //   this.getDefaultQrCodes(this.departmentIdInstallation);
        // }
      },
    },
    'item.department_id': {
      async handler(newVal, oldVal) {
        if (this.isInit) return;
        if (newVal !== oldVal) {
          await this.getDefaultQrCodes(newVal);
        }
      },
    },
  },
  created() {
    this.roles = this.user.roles.map((x) => x.id);
    this.item = { ...this.itemDto };
    this.init();
  },
  methods: {
    async init() {
      await this.getQrCodes();
    },

    async openFromTruckScheduleRequestForm(item, isView = false) {
      this.$refs.dialogBox.open();
      this.isView = isView;
      this.isInit = true;
      this.loading = true;
      this.item = { ...this.itemDto };
      // this.item.inventory_class_id = this.inventoryClassIdInst;
      if (this.qrCodes.length === 0) {
        await this.getQrCodes();
      }
      if (item) {
        this.item = JSON.parse(JSON.stringify(item));
        if (this.item.items.length > 0) {
          this.item.items.forEach((item, index) => {
            const inventoryTag = this.qrCodes.find(
              (qrCode) => qrCode.inventory_tag === item.inventory_tag
            ); // slow way
            item.quantity_on_hand = inventoryTag.quantity_on_hand;
            item.index = index;
            item.is_default =
              inventoryTag.item.departments &&
              inventoryTag.item.departments.length > 0;
            item.inventory_tag_item = inventoryTag;
          });
        }
      } else {
        this.item = { ...this.itemDto };
        this.item.items = [];
      }
      this.initBillOfLadingExtraItemForm(null, this.item.bill_of_lading_extra_items);
      this.loading = false;
    },

    async open(id = null, pickState = false, isView = false) {
      this.isInit = true;
      this.pickState = pickState;
      this.isView = isView;
      this.item = { ...this.itemDto };
      this.item.items = [];
      this.selectedPickListItems = [];
      this.pickedItems = [];
      this.$refs.dialogBox.open();
      this.$bus.$emit('loading');
      this.attachments = [];
      if (id) {
        this.item.id = id;
        const res = await this.get(id);
        this.attachments = await this.$api.attachment.read({
          filters: {
            record_id: id, 
            type_id: this.attachmentTypeIdPickList,
          },
          includes: {
            is_include_item_image: true,
          },
        });
      }
      this.isInit = false;
      this.getProjects();
      this.getJobs();
      this.getDepartments();
      this.initBillOfLadingExtraItemForm(id);
      this.$bus.$emit('loading-stop');
    },

    initBillOfLadingExtraItemForm(id = null, items = null) {
      this.$nextTick(async () => {
        if (this.$refs.billOfLadingExtraItemForm) {
          this.$refs.billOfLadingExtraItemForm.items = [];
          if (id) {
            this.$refs.billOfLadingExtraItemForm.get(id);
          } else if (items && items.length > 0) {
            this.$refs.billOfLadingExtraItemForm.items = items;
          }
          this.$refs.billOfLadingExtraItemForm.getExtraItemTypes();
          this.$refs.billOfLadingExtraItemForm.getColors();
          this.$refs.billOfLadingExtraItemForm.getUoms();
        }
      });
    },

    async get(id) {
      if (!id) return;
      this.loading = true;
      const submitModel = {
        filters: {
          id,
          // is_bill_of_lading: this.isSupplyingForBillOfLading ? 1 : null,
        },
        with: ['items']
      };
      const res = await this.$api.partPickList.readData(submitModel);
      const cancelCode = 3001;
      if (res === cancelCode) { return; }
      this.loading = false;
      if (!res || !res.data) {
        this.item = { ...this.itemDto };
        this.isInit = false;
        return;
      }
      const firstIndex = 0;
      this.item = res.data[firstIndex];

      await this.getQrCodes();

      // if (this.item.items.length > 0) {
      //   this.item.items.forEach((item, index) => {
      //     const inventoryTag = this.qrCodes.find((qrCode) => qrCode.inventory_tag === item.inventory_tag); // slow way
      //     if (!inventoryTag) {
      //       console.warn(`Inventory tag not found for item: ${item.inventory_tag}`);
      //       return;
      //     }
      //     item.quantity_on_hand = inventoryTag.quantity_on_hand;
      //     item.index = index;
      //     item.is_default = inventoryTag.item.departments && inventoryTag.item.departments.length > 0;
      //     item.inventory_tag_item = inventoryTag;
      //   });
      // }
      if (this.item.items.length > 0) {
        this.item.items.forEach((item, index) => {
          if (!item.inventory_tag_item) {
            console.warn(`Item at index ${index} does not have an inventory tag.`);
            return;
          }
          // item.quantity_on_hand = item.inventory_tag_item.quantity_on_hand;
          item.index = index;
          item.is_default = item.inventory_tag_item.item.departments && item.inventory_tag_item.item.departments.length > 0;
        });
      }
      this.isInit = false;
    },

    async getQrCodes() {
      // this.$bus.$emit('loading');
      this.loadingQrCodes = true;
      const readModel = {
        filters:
        {
          // inventory_class_id: this.item.truck_schedule_request_id ? this.inventoryClassIdInst : this.inventoryClassIdParts,
          // inventory_class_id: this.item.inventory_class_id,
          is_qty_on_hand_o: 1,
        },
        // includes: {
        //   is_include_item_image: true,
        //   is_include_supplier_label: true,
        // },
      };
      if (this.isCreatingFromTruckScheduleRequest) {
        const pickListInventoryClassIds = [this.inventoryClassId4500, this.inventoryClassId4800, this.inventoryClassIdInst, this.inventoryClassIdCommercial, this.inventoryClassIdParts];
        readModel.filters.class_ids = pickListInventoryClassIds;
        // readModel.filters.is_qty_on_hand_o = 0;
      } else {
        readModel.filters.inventory_class_id = this.item.inventory_class_id;
      }
      const res = await this.$api.inventoryTag.readData(readModel);
      // this.$bus.$emit('loading-stop');
      const cancelCode = 3001;
      if (res === cancelCode) return;
      if (!res || !res.data || res.data.length === 0) {
        this.qrCodes = [];
        this.loadingQrCodes = false;
        return;
      }

      this.qrCodes = res.data;
      this.loadingQrCodes = false;
    },

    async getProjects() {
      this.loadingProjects = true;
      const submitModel = {
        filters: {
          is_all: 1,
        },
      };
      const res = await this.$api.project.read(submitModel);
      const cancelCode = 3001;
      if (res === cancelCode) { return; }
      if (!res || !res.data) {
        this.projects = [];
        this.loadingProjects = false;
        return;
      }
      this.projects = res.data;
      this.loadingProjects = false;
    },

    async getJobs() {
      this.loadingJobs = true;
      const submitModel = {
        filters: {
          // inventory_class_id: this.inventoryClassId,
          // is_sub_class: 1
          project_id: this.item.project_id,
          // is_manual: 0,
        },
      };
      const res = await this.$api.job.readDDL(submitModel);
      const cancelCode = 3001;
      if (res === cancelCode) return;
      this.jobs = res;
      this.loadingJobs = false;
    },

    async getDepartments() {
      this.loadingDepartments = true;
      const submitModel = {
        filters: {
        },
      };
      if (this.item.inventory_class_id === this.inventoryClassIdInst) {
        submitModel.filters.id = [this.departmentId4800Installation, this.departmentId6000Installation];
      }
      this.departments = await this.$api.department.read(submitModel);
      if (!this.pickState && this.item.id && this.item.department_id && !this.isView) {
        this.getDefaultQrCodes(this.item.department_id);
      }
      this.loadingDepartments = false;
    },

    async getDefaultQrCodes(departmentId) {
      // if (!this.item.id) this.item.items = [];
      //remove default items from data-table
      if (!this.item.id) {
        this.item.items = this.item.items.filter((x) => !x.is_default);
      }

      const department = this.departments.find((x) => x.id === departmentId);

      const inventoryItemIds = department.inventory_items.map((x) => x.id);

      if (inventoryItemIds.length === 0) return;

      this.loadingDefaultQrCodes = true;
      const readModel = {
        filters:
        {
          inventory_class_id: this.item.inventory_class_id,
          inventory_item_ids: inventoryItemIds,
        },
        sort: {
          order_by_first: 'inventory_item_name',
          direction_sort: false,
        },
        // includes: {
        //   is_include_item_image: true,
        //   is_include_supplier_label: true,
        // },
      };
      const res = await this.$api.inventoryTag.readData(readModel);

      const cancelCode = 3001;
      if (res === cancelCode) return;
      if (!res || !res.data || res.data.length === 0) {
        this.loadingDefaultQrCodes = false;
        return;
      }
      this.loadingDefaultQrCodes = false;

      // add default inventory tags to data-table
      const inventoryTags = res.data;
      inventoryTags.forEach((inventoryTag) => {
        const addItemDto = {
          id: 0,
          inventory_tag: inventoryTag.inventory_tag,
          quantity_required: null,
          quantity_on_hand: inventoryTag.quantity_on_hand,
          is_default: true,
          inventory_tag_item: inventoryTag,
          index: this.item.items.length,
        };
        this.save(addItemDto, false);
      });
    },

    handleSubmit() {
      if (this.pickState) {
        if (this.isProofOfWorkNeeded) {
          return this.submitProofOfWork();
        }
        return this.closeForm();
      }
      else if (this.isSupplyingForBillOfLading) {
        return this.submitBillOfLadingPickListItems();
      } else if (this.isCreatingFromTruckScheduleRequest) {
        return this.submitTruckScheduleRequestPickListItems();
      } else {
        return this.submit();
      }
    },

    submitBillOfLadingPickListItems() {
      const filteredSelectedPickListItems = this.selectedPickListItems.filter((item) => item.bill_of_lading_id === null);
      const filteredSelectedCustomItems = this.$refs.billOfLadingExtraItemForm.selectedCustomItems.filter((item) => item.bill_of_lading_id === null);
      const filteredSelectedItemsAndPickListJob = {
        pick_list_items: filteredSelectedPickListItems,
        custom_items: filteredSelectedCustomItems,
        job_id: this.item.job_id,
      }
      // this.$emit('success', filteredSelectedPickListItems);
      this.$emit('success', filteredSelectedItemsAndPickListJob);
      return true;
    },

    submitTruckScheduleRequestPickListItems() {
      if (this.item.items.length === 0 && !this.isCreatingFromTruckScheduleRequest) {
        this.$bus.$emit('showError', 'Please add at least one part.');
        return false;
      }
      this.removeZeroQuantityRequiredRecords();
      if (this.$refs.billOfLadingExtraItemForm) {
        this.item.bill_of_lading_extra_items = this.$refs.billOfLadingExtraItemForm.items;
      }
      this.$emit('success', JSON.parse(JSON.stringify(this.item)));
      return true;
    },

    async submit() {
      if (this.item.id && this.item.items.length === 0 && (this.$refs.billOfLadingExtraItemForm.items.length === 0)) {
        const confirm = await this.$refs.confirmForm.open({
          title: 'Submitting an empty pick list will delete the pick list. Do you want to continue?',
        });
        if (!confirm) {
          return false;
        }
      }
      if (this.pickState && (this.item.status_id === this.statusIdPartiallySupplied || this.item.status_id === this.statusIdSupplied)) {
        this.$emit('success');
        return true;
      }

      if (this.$refs.billOfLadingExtraItemForm) {
        this.item.bill_of_lading_extra_items = this.$refs.billOfLadingExtraItemForm.items;
      }

      if ((this.item.id === null || this.item.id === 0 || this.item.id === undefined) && this.item.items.length === 0 && (!this.item.bill_of_lading_extra_items || this.item.bill_of_lading_extra_items.length === 0)) {
        this.$bus.$emit('showError', 'Please add at least one item or one extra item');
        return false;
      }

      this.removeZeroQuantityRequiredRecords();

      let res;
      if (this.item.id) {
        res = await this.$api.partPickList.update(this.item);
      } else {
        res = await this.$api.partPickList.store(this.item);
      }

      if (!res) {
        this.$bus.$emit('showError');
        return false;
      }

      if (this.item.note && this.item.note.trim().length > 0 && this.isNewNote(this.item.note)) {
        const note = {
          text: this.item.note,
          record_id: res,
          type_id: this.noteTypeIdPickList,
        };
        const res2 = await this.$api.note.store(note);
        if (!res2) {
          this.$bus.$emit(
            'showError',
            'Error adding note to Pick List'
          );
          return false;
        }
      }

      await this.saveAttachments(res);

      //soft delete part pick list if all items (including extra items) are removed
      if (this.item.items.length === 0 && (!this.item.bill_of_lading_extra_items || this.item.bill_of_lading_extra_items.length === 0)) {
        await this.$api.partPickList.delete(this.item.id);
      }

      this.$bus.$emit('showSuccess');
      this.$emit('success');
      return res;
    },

    async submitProofOfWork() {
      // if (this.attachment == null) {
      //   this.$bus.$emit('showError', 'Proof of work is required before submitting the pick list.');
      //   return false;
      // }
      if (this.attachment == null) {
        return this.closeForm();
      }
      if (this.item == null || this.item.id == null || this.item.id === 0) {
        this.$bus.$emit('showError', 'Something went wrong... Pick List ID is required to submit proof of work.');
        return false;
      }
      this.$bus.$emit('loading');
      const formData = new FormData();
      formData.append('file', this.attachment);
      formData.append('record_id', this.item.id);
      formData.append('type_id', this.attachmentTypeIdPickList);
      const res = await this.$api.attachment.store(formData);
      this.$bus.$emit('loading-stop');
      if (!res) {
        this.$bus.$emit('showError');
        return false;
      }
      await this.saveAttachments(res.id);
      this.$bus.$emit('showSuccess');
      this.$emit('success');
      return res;
    },

    async supply() {
      const res = await this.$api.partPickList.get(`supply/${this.item.id}`);
      if (!res) {
        this.$bus.$emit('showError');
        return;
      }

      this.$bus.$emit('showSuccess');
    },

    async submitPick() {
      const isNotSupplied = this.item.items.some((item) => item.status && item.status.id !== this.itemStatusIdSupplied);
      if (isNotSupplied) {
        this.$bus.$emit('showError', 'All items must be fully supplied');
        return;
      }
      const res = await this.$api.partPickList.store(this.item, 'supply');
      if (!res) {
        this.$bus.$emit('showError');
        return;
      }

      this.$bus.$emit('showSuccess');
    },

    add() {
      this.$refs.itemForm.open(null, this.item.items.length, this.isCreatingFromTruckScheduleRequest ? null : this.item.inventory_class_id);
    },

    save(item, isShowError = true) {
      if (!item) return;
      // eslint-disable-next-line no-param-reassign
      const existingItemIndex = this.item.items.findIndex((x) => x.inventory_tag === item.inventory_tag);
      if (existingItemIndex >= 0 && existingItemIndex !== item.index) {
        if (isShowError) this.$bus.$emit('showError', 'Item already exists.');
        return;
      }
      // check to see if we're editing an existing item's inventory_tag or quantity_required. If yes, then change status and quantity_supplied to null
      if (item.index < this.item.items.length) {
        const editItem = this.item.items[item.index];
        if (editItem.inventory_tag !== item.inventory_tag || editItem.quantity_required !== item.quantity_required) {
          item.status = null;
          item.quantity_supplied = null;
        }
      }
      if (item.index !== undefined && item.index !== null) {
        this.$set(this.item.items, item.index, JSON.parse(JSON.stringify(item)));
      } else {
        item.index = this.item.items.length;
        this.item.items.push(JSON.parse(JSON.stringify(item)));
      }
    },

    openEditItem(inventoryTag) {
      const itemIndex = this.item.items.findIndex((x) => x.inventory_tag === inventoryTag);
      if (itemIndex < 0) return;
      this.$refs.itemForm.open(this.item.items[itemIndex], itemIndex, this.item.inventory_class_id);
    },

    async deleteItem(item) {
      const confirm = await this.$refs.confirmForm.open({
        title: 'Are you sure that you want to delete this record?',
      });
      if (!confirm) return;
      const itemIndex = this.item.items.findIndex((x) => x.inventory_tag === item.inventory_tag);
      if (itemIndex === -1) return;
      this.item.items.splice(itemIndex, 1);
    },

    getQRCode(qrCode) {
      // eslint-disable-next-line eqeqeq
      const item = this.qrCodes.find((x) => x.inventory_tag == qrCode);
      if (!item) return '';
      return item.inventory_tag;
    },

    getItemName(qrCode) {
      // eslint-disable-next-line eqeqeq
      // const item = this.qrCodes.find((x) => x.inventory_tag == qrCode);
      // if (!item) return '';
      // return item.item_full_name;
      return qrCode ? qrCode.item.material_id + ' - ' + qrCode.item.name : '';
    },

    pick(item, isEditPick = false) {
      this.$refs.itemViewForm.open(item, isEditPick);
    },

    getTotalRequired() {
      let total = 0;
      this.item.items.forEach((item) => {
        if (item.quantity_required) total += parseInt(item.quantity_required, 10);
      });
      return total;
    },

    getTotalSupplied() {
      let total = 0;
      this.item.items.forEach((item) => {
        if (item.quantity_supplied) total += parseInt(item.quantity_supplied, 10);
      });
      return total;
    },

    openNoteForm() {
      this.$refs.noteForm.open(
        this.item.id,
        this.noteTypeIdPickList
      );
    },

    handleNoteSuccess(id, text) {
      if (!id || !text) {
        this.item.latest_note = null;
      }
      this.$set(this.item, 'note', text);
      this.$set(this.item, 'latest_note', { id, text });
      // this.item.note = text;
      // this.item.latest_note = { id, text };
    },

    isNewNote(note) {
      if (!this.item.latest_note) return true;
      return this.item.latest_note.text !== note;
    },

    // openItemShowImageForm(qrCode) {
    //   const item = this.qrCodes.find((x) => x.inventory_tag === qrCode);
    //   if (!item || !item.item) return;
    //   this.$refs.itemShowImageForm.openDialog(item.item.photo, item.item.mimeType, qrCode);
    // },
    async openItemShowImageForm(item) {
      const inventoryItemId = item?.inventory_tag_item?.inventory_item_id;
      if (!inventoryItemId) {
        this.$bus.$emit('showError', 'Could not retrieve photo');
        return;
      }
      this.$bus.$emit('loading');
      const readModel = {
        filters:
        {
          id: inventoryItemId,
        },
        includes: { is_include_item_image: true },
      };
      const res = await this.$api.inventoryItem.readData(readModel);
      const cancelCode = 3001;
      if (res === cancelCode) return;
      this.$bus.$emit('loading-stop');
      if (!res || !res.data || res.data.length === 0) {
        return;
      }
      const firstIndex = 0;
      const inventoryItem = res.data[firstIndex];
      if (!inventoryItem.photo) {
        this.$bus.$emit('showError', 'No photo available for this item');
        return;
      }
      this.$refs.itemShowImageForm.openDialog(inventoryItem.photo, inventoryItem.mime_type, inventoryItem.material_id);
    },

    convertedPhoto(qrCode) {
      const item = this.qrCodes.find((x) => x.inventory_tag === qrCode);
      if (!item || !item.item) return '';

      return `data:${item.item.mimeType};base64, ${item.item.photo}`;
    },

    removeZeroQuantityRequiredRecords() {
      this.item.items = this.item.items.filter((item) => item.quantity_required > 0 && item.quantity_required !== null);
    },

    async supplySuccess(pickListItem) {
      // const res = await this.getQrCodes();
      this.$bus.$emit('loading');
      if (Array.isArray(this.pickedItems)) {
        this.pickedItems.push(pickListItem);
      } else {
        this.pickedItems = [pickListItem];
      }
      await this.get(this.item.id);
      this.$bus.$emit('loading-stop');
    },

    async customItemSupplySuccess(customItem) {
      this.$bus.$emit('loading');
      customItem.quantity_supplied = 1;
      if (Array.isArray(this.pickedItems)) {
        this.pickedItems.push(customItem);
      } else {
        this.pickedItems = [customItem];
      }
      this.$bus.$emit('loading-stop');
    },

    async closeForm() {
      this.$emit('success');
      return true;
    },

    onProjectChange() {
      this.item.job_id = null;
      this.getJobs();
    },

    onJobChange(jobId) {
      if (!jobId) return;
      const job = this.jobs.find((x) => x.id === jobId);
      this.item.project_id = job.project_id;
    },

    viewProofOfWork() {
      this.$refs.attachmentForm.open(this.item.id, this.attachmentTypeIdPickList);
    },
    addAttachment() {
      if (this.attachmentInput === null) return;
      this.attachments.push(this.attachmentInput);
      this.attachmentInput = null;
    },

    async deleteAttachment(attachmentIdx) {
      const attachment = this.attachments[attachmentIdx];
      const confirm = await this.$refs.confirmForm.open({ title: 'Are you sure that you want to delete this attachment?' });
      if (!confirm) return;
      if (attachment.id) {
        const res = await this.$api.attachment.softDelete(attachment.id);
        if (!res) {
          this.$bus.$emit('showError');
          return;
        }
      }
      this.$bus.$emit('showSuccess');
      this.attachments.splice(attachmentIdx, 1);
    },

    async downloadAttachment(file) {
      const res = await this.$api.attachment.get(`download/${file.id}`, { responseType: 'blob' });
      const downloadedFile = new Blob([res]);
      saveAs(downloadedFile, file.file_name);
    },

    async saveAttachments(id) {
      const attachmentsToSave = this.attachments.filter(x => !x.id);
      for (let i = 0; i < attachmentsToSave.length; i++) {
        const formData = new FormData();
        formData.append('file', attachmentsToSave[i]);
        formData.append('record_id', id);
        formData.append('type_id', this.attachmentTypeIdPickList);
        const res = await this.$api.attachment.store(formData);
      }
    }
  },
};
</script>

<style scoped>
.profile-photo {
  max-width: 60px;
  max-height: 60px;
  margin-right: 10px;
  display: inline-block;
}
</style>
